import { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSearchConditionForTree,
  selectSearchFocusIndexForTree,
  selectSearchItemHit,
  selectSearchMatchesCountForTree,
  selectSearchQueryForTree,
} from '../../../store/selectors/tree/tree';
import {
  selectSearchAvailableFlags,
  selectSearchCatalogList,
} from '../../../store/selectors/tree/search';
import { SEARCH_BY } from '../../../utils/tree/search';

import arrowRight from '@ingka/ssr-icon/paths/arrow-right';
import chrevronDown from '@ingka/ssr-icon/paths/chevron-down-small';
import chrevronUp from '@ingka/ssr-icon/paths/chevron-up-small';
import crossSmall from '@ingka/ssr-icon/paths/cross-small';

import styles from './SearchPanel.module.scss';
import SearchPanelButton from './SearchPanelButton';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { isLoadingSelector } from '../../../store/slices/pond';
import type { AppThunkDispatch, RootState } from '../../../store';
import { useClickOutside } from '../../../hooks/useClickOutside';
import {
  clearSearch,
  moveSearchNext,
  moveSearchPrevious,
  search,
  searchBySuggestionId,
  searchSetConditionByFlag,
  searchSetConditionByText,
  selectSearchSuggestionsForTree,
  toggleSearchPanel,
} from '../../../store/slices/treeSearch';

import SearchSuggestions from './SearchSuggestions';
import SearchFlag from './SearchFlag';

const SearchPanel = function SearchPanel({ tree }: { tree: string }) {
  const hideSuggestions = () => {
    setActive(false);
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef(null);
  const ignoreNextDebounce = useRef(false);
  const [active, setActive] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch<AppThunkDispatch>();
  useClickOutside(wrapperRef, () => hideSuggestions());

  const catalogList = useSelector((state: RootState) =>
    selectSearchCatalogList(state, tree),
  );

  const suggestions = useSelector((state: RootState) =>
    selectSearchSuggestionsForTree(state, tree),
  );

  const availableFlags = useSelector((state: RootState) =>
    selectSearchAvailableFlags(state, tree),
  );

  const searchCondition = useSelector((state: RootState) =>
    selectSearchConditionForTree(state, tree),
  );
  const searchQuery = useSelector((state: RootState) =>
    selectSearchQueryForTree(state, tree),
  );

  const structureReasonText = useMemo(
    () => searchQuery?.structureReasonText,
    [searchQuery],
  );
  const isSearching = useSelector((state: RootState) =>
    isLoadingSelector(state, searchCondition?.condition),
  );
  const item = useSelector((state: RootState) => selectSearchItemHit(state, tree));

  const numberOfSearchResults = useSelector((state: RootState) =>
    selectSearchMatchesCountForTree(state, tree),
  );
  const searchFocusIndex = useSelector((state: RootState) =>
    selectSearchFocusIndexForTree(state, tree),
  );

  const selectableSuggestions = useMemo(
    () => [
      ...suggestions.map(({ id, type }) => ({ id, type })),
      ...Object.keys(availableFlags).map((flag) => ({
        type: SEARCH_BY.FLAG,
        id: flag,
      })),
    ],
    [availableFlags, suggestions],
  );

  const debouncedValue = useDebounceValue(searchText, 300);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (ignoreNextDebounce.current) {
      ignoreNextDebounce.current = false;
      return;
    }
    dispatch(searchSetConditionByText(tree, debouncedValue));
  }, [debouncedValue, tree, dispatch]);

  const setInput = (text: string) => {
    setSearchText(text);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getSelectedSuggestion = () => {
    if (selectedIndex === null) return null;

    const selected = selectableSuggestions[selectedIndex];

    if (!selected) {
      return null;
    }
    if (selected.type === SEARCH_BY.FLAG) {
      return selected.id;
    }
    return suggestions.find((s) => s.id === selected.id)?.id;
  };

  const showSuggestions = () => {
    setActive(true);
  };

  const onClearBtnClick = () => {
    dispatch(clearSearch(tree));
    if (inputRef.current) {
      setSearchText('');
      inputRef.current.focus();
    }
  };

  const onCloseBtnClick = () => {
    dispatch(toggleSearchPanel(tree, false));
  };

  const highlightNextSuggestion = () => {
    const nextSelectedIndex = selectedIndex + 1;
    if (!selectableSuggestions.length) {
      return;
    }
    setSelectedIndex(
      nextSelectedIndex === selectableSuggestions.length ? 0 : nextSelectedIndex,
    );
  };

  const highlightPrevSuggestion = () => {
    const prevSelectedIndex = selectedIndex - 1;
    if (!selectableSuggestions.length) {
      return;
    }
    setSelectedIndex(
      prevSelectedIndex === -1
        ? selectableSuggestions.length - 1
        : prevSelectedIndex,
    );
  };

  const onType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setSelectedIndex(-1);
  };

  const onSearch = () => {
    dispatch(search(tree));
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const selectNextSearchHit = () => {
    dispatch(moveSearchNext(tree));
  };

  const selectPreviousSearchHit = () => {
    dispatch(moveSearchPrevious(tree));
  };

  const setSearchByFlag = (flag: string) => {
    dispatch(searchSetConditionByFlag(tree, flag));
    setSelectedIndex(-1);
    onSearch();
    setSearchText('');
    setActive(false);
  };

  const selectSuggestionById = (suggestionId: string, suggestionTitle: string) => {
    ignoreNextDebounce.current = true;
    dispatch(searchBySuggestionId(tree, suggestionId, suggestionTitle));
    setInput(suggestionTitle);
    inputRef.current?.focus();
    setSelectedIndex(-1);
    setActive(false);
  };

  const onSelectFlag = (flag: string) => {
    setSearchByFlag(flag);
  };

  const selectSuggestion = () => {
    const selected = getSelectedSuggestion();
    const foundSuggestion = suggestions.find((s) => s.id === selected);
    if (foundSuggestion) {
      selectSuggestionById(foundSuggestion.id, foundSuggestion.title);
    } else if (selected && availableFlags[selected]) {
      setSearchByFlag(selected);
    }
    setSelectedIndex(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      highlightNextSuggestion();
    } else if (e.key === 'ArrowUp') {
      highlightPrevSuggestion();
    } else if (e.key === 'Enter') {
      if (selectedIndex < 0) {
        if (searchCondition?.condition) {
          onSearch();
          setActive(false);
        }
      } else {
        selectSuggestion();
      }
    } else if (e.key === 'Escape') {
      onCloseBtnClick();
    } else {
      return true;
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const emptyResult = !!(
    searchQuery &&
    !numberOfSearchResults &&
    !catalogList.length
  );

  const selectedSuggestion = getSelectedSuggestion();
  const disableNavigation = !numberOfSearchResults;
  const displayFlagContainer = searchCondition?.by === SEARCH_BY.FLAG;
  const placeholder = active ? 'Find item or category name/ID' : 'Find';

  return (
    <div ref={wrapperRef} className={cx(styles.panel, { [styles.active]: active })}>
      <SearchPanelButton
        className={styles.searchClose}
        onClick={onCloseBtnClick}
        iconPath={crossSmall}
        label="Close search"
      />
      <div className={styles.wrapper}>
        <div className={styles.searchComponent}>
          <div
            className={cx(styles.inputWrapper, {
              [styles.empty]: !searchCondition,
            })}
          >
            <input
              ref={inputRef}
              id="searchInput"
              name="Search input"
              autoComplete="off"
              className={cx({
                [styles.hidden]: displayFlagContainer,
              })}
              type="text"
              placeholder={displayFlagContainer ? '' : placeholder}
              onChange={onType}
              onFocus={showSuggestions}
              onKeyDown={onKeyDown}
              value={searchText}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={active}
              aria-controls="suggestions-list"
              aria-activedescendant={selectedSuggestion || undefined}
            />
            {displayFlagContainer && (
              <div className={styles.flagContainer}>
                <SearchFlag flag={searchCondition.condition} />
              </div>
            )}
          </div>
          {!!searchCondition && (
            <>
              <SearchPanelButton
                onClick={onClearBtnClick}
                iconPath={crossSmall}
                label="Clear search"
                disabled={!searchCondition}
              />
              {!searchQuery && (
                <>
                  <div className={styles.divider} />
                  <SearchPanelButton
                    className={styles.curvedRight}
                    onClick={onSearch}
                    iconPath={arrowRight}
                    label="Perform search"
                    disabled={!searchCondition}
                  />
                </>
              )}
            </>
          )}
          {searchQuery && (
            <>
              <div
                className={cx(styles.nbrOfHits, {
                  [styles.active]: !disableNavigation,
                })}
              >
                {`${
                  searchFocusIndex === null || searchFocusIndex < 0
                    ? 0
                    : searchFocusIndex + 1
                }/${numberOfSearchResults}`}
              </div>

              <>
                <SearchPanelButton
                  className={cx({ [styles.active]: !disableNavigation })}
                  onClick={selectPreviousSearchHit}
                  iconPath={chrevronUp}
                  label="Select previous"
                  disabled={disableNavigation}
                />
                <SearchPanelButton
                  className={cx(
                    { [styles.active]: !disableNavigation },
                    styles.curvedRight,
                  )}
                  onClick={selectNextSearchHit}
                  iconPath={chrevronDown}
                  label="Select next"
                  disabled={disableNavigation}
                />
              </>
            </>
          )}
        </div>
      </div>
      <SearchSuggestions
        isSearching={isSearching}
        emptyResult={emptyResult}
        structureReasonText={structureReasonText}
        searchConditionString={searchCondition?.condition}
        item={item}
        catalogList={catalogList}
        suggestions={suggestions}
        availableFlags={availableFlags}
        selectedSuggestion={selectedSuggestion}
        active={active}
        onSelectSuggestion={selectSuggestionById}
        onSelectFlag={onSelectFlag}
        setSelectedIndex={setSelectedIndex}
      />
    </div>
  );
};

export default SearchPanel;
